import { call, put, takeLatest } from 'redux-saga/effects';
import { createRequestSaga } from '@/redux/helpers';
import Api from '@/api';
import { addToastsAction } from '@/redux/toasts';
import { mapErrorToastsData } from '@/lib/api';
import { toastType } from '@/components/common';
import {
  pushToAspynAsync,
  seedPrerequisiteEntitiesAsync,
  clearCacheOfAspynPushesAsync,
  requestMigrationJobsAsync,
  migrateRepsAsync,
  migrateHousingAsync,
  getPatAsync,
  updatePatAsync,
  removePatAsync,
} from './actions';

function* pushToAspynSaga({ payload }) {
  try {
    const { userId } = payload;

    const response = yield call(Api.pushToAspyn, { user_id: userId });

    yield put(pushToAspynAsync.success(response));
  } catch (error) {
    yield put(pushToAspynAsync.failure(error));
    throw error;
  }
}

function* seedPrerequisiteEntitiesSaga() {
  try {
    const response = yield call(Api.seedPrerequisiteEntities);

    yield put(seedPrerequisiteEntitiesAsync.success(response));
    yield put(addToastsAction([{
      type: toastType.SUCCESS,
      message: response?.message || 'Prerequisite entities were seeded successfully.',
    }]));
  } catch (error) {
    yield put(seedPrerequisiteEntitiesAsync.failure(error));
    yield put(addToastsAction(mapErrorToastsData(error)));
    throw error;
  }
}

function* clearCacheOfAspynPushesSaga() {
  try {
    const response = yield call(Api.clearCacheOfAspynPushes);

    yield put(clearCacheOfAspynPushesAsync.success(response));
    yield put(addToastsAction([{
      type: toastType.SUCCESS,
      message: response?.message || 'Cache was cleared successfully.',
    }]));
  } catch (error) {
    yield put(clearCacheOfAspynPushesAsync.failure(error));
    yield put(addToastsAction(mapErrorToastsData(error)));
    throw error;
  }
}

function* requestMigrationJobsSaga({ payload }) {
  try {
    const response = yield call(Api.getMigrationJobs, payload);

    yield put(requestMigrationJobsAsync.success(response));
  } catch (error) {
    yield put(requestMigrationJobsAsync.failure(error));
    yield put(addToastsAction(mapErrorToastsData(error)));
    throw error;
  }
}

function* migrateRepsSaga({ payload }) {
  try {
    const { userIds } = payload;

    const response = yield call(Api.migrateReps, { user_ids: userIds });

    yield put(migrateRepsAsync.success(response));
    yield put(addToastsAction([{
      type: toastType.SUCCESS,
      message: response?.message || 'Rep migration jobs were dispatched successfully.',
    }]));
  } catch (error) {
    yield put(migrateRepsAsync.failure(error));
    yield put(addToastsAction(mapErrorToastsData(error)));
    throw error;
  }
}

function* migrateHousingSaga({ payload }) {
  try {
    const { recruitingSeasonId } = payload;

    const response = yield call(Api.migrateHousing, { recruiting_season_id: recruitingSeasonId });

    yield put(migrateHousingAsync.success(response));
    yield put(addToastsAction([{
      type: toastType.SUCCESS,
      message: response?.message || 'Housing migration job was dispatched successfully.',
    }]));
  } catch (error) {
    yield put(migrateHousingAsync.failure(error));
    yield put(addToastsAction(mapErrorToastsData(error)));
    throw error;
  }
}

function* getPatSaga() {
  try {
    const response = yield call(Api.getPat);

    yield put(getPatAsync.success(response));
  } catch (error) {
    yield put(getPatAsync.failure(error));
    yield put(addToastsAction(mapErrorToastsData(error)));
    throw error;
  }
}

function* updatePatSaga({ payload }) {
  try {
    const response = yield call(Api.updatePat, payload);

    yield put(updatePatAsync.success(response));
    yield put(addToastsAction([{
      type: toastType.SUCCESS,
      message: response?.message || 'Personal Access Token was updated successfully.',
    }]));
  } catch (error) {
    yield put(updatePatAsync.failure(error));
    yield put(addToastsAction(mapErrorToastsData(error)));
    throw error;
  }
}

function* removePatSaga({ payload }) {
  try {
    const response = yield call(Api.removePat, payload);

    yield put(removePatAsync.success());
    yield put(addToastsAction([{
      type: toastType.SUCCESS,
      message: response?.message || 'Personal Access Token was removed successfully.',
    }]));
  } catch (error) {
    yield put(removePatAsync.failure());
    yield put(addToastsAction(mapErrorToastsData(error)));
    throw error;
  }
}

export function* aspynMigratorActionWatcher() {
  yield takeLatest(
    pushToAspynAsync.request,
    createRequestSaga(pushToAspynSaga, {
      keyNew: 'pushToAspyn',
      errKey: 'pushToAspyn',
    }),
  );

  yield takeLatest(
    seedPrerequisiteEntitiesAsync.request,
    createRequestSaga(seedPrerequisiteEntitiesSaga, {
      keyNew: 'seedPrerequisiteEntities',
      errKey: 'seedPrerequisiteEntities',
    }),
  );

  yield takeLatest(
    clearCacheOfAspynPushesAsync.request,
    createRequestSaga(clearCacheOfAspynPushesSaga, {
      keyNew: 'clearCacheOfAspynPushes',
      errKey: 'clearCacheOfAspynPushes',
    }),
  );

  yield takeLatest(
    requestMigrationJobsAsync.request,
    createRequestSaga(requestMigrationJobsSaga, {
      keyNew: 'requestMigrationJobs',
      errKey: 'requestMigrationJobs',
    }),
  );

  yield takeLatest(
    migrateRepsAsync.request,
    createRequestSaga(migrateRepsSaga, {
      keyNew: 'migrateReps',
      errKey: 'migrateReps',
    }),
  );

  yield takeLatest(
    migrateHousingAsync.request,
    createRequestSaga(migrateHousingSaga, {
      keyNew: 'migrateHousing',
      errKey: 'migrateHousing',
    }),
  );

  yield takeLatest(
    getPatAsync.request,
    createRequestSaga(getPatSaga, {
      keyNew: 'getPat',
      errKey: 'getPat',
    }),
  );

  yield takeLatest(
    updatePatAsync.request,
    createRequestSaga(updatePatSaga, {
      keyNew: 'updatePat',
      errKey: 'updatePat',
    }),
  );

  yield takeLatest(
    removePatAsync.request,
    createRequestSaga(removePatSaga, {
      keyNew: 'removePat',
      errKey: 'removePat',
    }),
  );
}
